import React, { useEffect, useState } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { useTranslation } from '../../hooks/useTranslation';
import { resourcesService } from '../../services/resources.service';
import LoadingState from '../../components/ui/LoadingState';
import { Link } from 'react-router-dom';
import { ROUTES } from '../../lib/routes';

const { FiPlus, FiEdit2, FiExternalLink, FiSearch } = FiIcons;

export default function ResourceManagement() {
  const { contentLanguage } = useTranslation();
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadResources();
  }, []);

  const loadResources = async () => {
    try {
      setLoading(true);
      const data = await resourcesService.getAllResourcesAdmin();
      setResources(data || []);
    } catch (err) {
      console.error('Error loading resources:', err);
      setResources([]);
    } finally {
      setLoading(false);
    }
  };

  const filteredResources = resources.filter((res) => {
    const trans =
      res.translations?.find((t) => t.language_code === contentLanguage) || res.translations?.[0];
    const text = (trans?.title || res.slug || '').toLowerCase();
    return text.includes(searchTerm.toLowerCase());
  });

  if (loading) return <LoadingState />;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-4">
        <div>
          <h1 className="text-3xl font-black text-slate-900 tracking-tight">Resource Management</h1>
          <p className="text-slate-500 mt-1">Create and manage content across all languages.</p>
        </div>
        <Link
          to={`${ROUTES.ADMIN.CONTENT}/new`}
          className="inline-flex items-center px-6 py-3 bg-blue-600 text-white font-bold rounded-2xl hover:bg-blue-700 transition-all shadow-lg shadow-blue-500/20"
        >
          <SafeIcon icon={FiPlus} className="mr-2" />
          New Resource
        </Link>
      </div>

      <div className="bg-white rounded-[2rem] shadow-sm border border-slate-100 overflow-hidden">
        <div className="p-6 border-b border-slate-100 bg-slate-50/50 flex gap-4">
          <div className="relative flex-1">
            <SafeIcon icon={FiSearch} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder="Search resources..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-3 bg-white border border-slate-200 rounded-xl focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 outline-none transition-all font-medium"
            />
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="bg-slate-50/50 text-slate-400 text-xs font-black uppercase tracking-widest">
                <th className="px-8 py-4">Resource</th>
                <th className="px-8 py-4">Status</th>
                <th className="px-8 py-4">Languages</th>
                <th className="px-8 py-4">Last Updated</th>
                <th className="px-8 py-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filteredResources.map((res) => {
                const trans =
                  res.translations?.find((t) => t.language_code === contentLanguage) || res.translations?.[0];

                return (
                  <tr key={res.id} className="hover:bg-slate-50/50 transition-colors group">
                    <td className="px-8 py-6">
                      <div className="font-bold text-slate-900">{trans?.title || 'Untitled'}</div>
                      <div className="text-xs text-slate-400 font-mono mt-1">{res.slug}</div>
                    </td>
                    <td className="px-8 py-6">
                      <span
                        className={`px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-tighter ${
                          res.status === 'published'
                            ? 'bg-green-100 text-green-700'
                            : 'bg-amber-100 text-amber-700'
                        }`}
                      >
                        {res.status}
                      </span>
                    </td>
                    <td className="px-8 py-6">
                      <div className="flex gap-1">
                        {res.translations?.map((t) => (
                          <span
                            key={t.language_code}
                            className="w-6 h-6 flex items-center justify-center bg-slate-100 text-[10px] font-bold rounded-md uppercase"
                          >
                            {t.language_code}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-8 py-6 text-sm text-slate-500 font-medium">
                      {new Date(res.updated_at).toLocaleDateString()}
                    </td>
                    <td className="px-8 py-6 text-right">
                      <div className="flex justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                        <Link
                          to={`${ROUTES.ADMIN.CONTENT}/edit/${res.id}`}
                          className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all"
                        >
                          <SafeIcon icon={FiEdit2} />
                        </Link>
                        <Link
                          to={ROUTES.PUBLIC.RESOURCE_DETAIL(res.slug)}
                          className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-all"
                        >
                          <SafeIcon icon={FiExternalLink} />
                        </Link>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {filteredResources.length === 0 && (
                <tr>
                  <td colSpan="5" className="px-8 py-12 text-center text-slate-400">
                    No resources found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}